import { Clazz, deserialize, getDefaultModelSchema, list, object, PropSchema, serialize } from 'serializr';

import {
  createImmutableDate,
  createImmutableList,
  createImmutableMap,
  createImmutableWeakTypedMap,
  IImmutableList,
  IImmutableMap,
  IImmutableWeakTypedMap,
  ImmutableDate,
  ImmutablePropsMap
} from '../state/redux';
import { isNullOrUndefined } from '../utils';
// import { invariant } from '../utils/assert'

export { alias, custom, deserialize, identifier, primitive, serializable, serialize, list } from 'serializr';

// for the generic type def below
// tslint:disable: no-any
type SerializerType = (value: any) => any;
type DeserializerType = (value: any, done: Function, contextObj: any) => void;
export type PropSchemaType = {
  serializer: SerializerType;
  deserializer: DeserializerType;
  jsonname?: string;
};

// tslint:disable-next-line: interface-name
export interface ImmutableModelConstructor<T extends ImmutablePropsMap<T>> {
  new (...args: any[]): T;
}

export function serializeFromImmutableMap(
  modelClass: ImmutableModelConstructor<any>,
  instance: IImmutableMap<any>
): any {
  return serialize(getDefaultModelSchema(modelClass), instance.toObject());
}

export function deserializeToImmutableMap<T extends ImmutablePropsMap<T>>(
  modelClass: ImmutableModelConstructor<T>,
  json: any,
  callback?: (err: any, result: any) => void
): IImmutableMap<T> {
  return createImmutableMap(deserialize(modelClass as any, json, callback) as any);
}

export function deserializeToImmutableList<T extends ImmutablePropsMap<T>>(
  modelClass: ImmutableModelConstructor<T>,
  response: any[]
): IImmutableList<IImmutableMap<T>> {
  return createImmutableList(
    response.map((item: any) => {
      return createImmutableMap(deserialize(modelClass as any, item) as any);
    })
  );
}

export function immutableList(propSchema: PropSchema): PropSchemaType {
  return {
    serializer: (listObj: IImmutableList<any>) => {
      if (listObj === null || listObj === undefined) {
        return listObj;
      }
      // invariant(List.isList(listObj), 'expected immutablelist')
      return list(propSchema as any).serializer(listObj.toArray());
    },
    deserializer: (jsonArray: Array<any>, done: Function, context: any) => {
      if (jsonArray === null || jsonArray === undefined) {
        done(null, jsonArray);
        return;
      }
      list(propSchema as any).deserializer(
        jsonArray,
        (err: Error, result: any) => {
          done(err, createImmutableList(result));
        },
        context,
        undefined
      );
    }
  };
}

export function immutableMap<T>(modelClass: Clazz<T>, allowNull: boolean = false): PropSchemaType {
  return {
    serializer: (item: IImmutableMap<any>) => {
      const modelSchema = getDefaultModelSchema(modelClass);
      if (item === null || item === undefined) {
        return item;
      }
      return serialize(modelSchema, item.toObject());
    },
    deserializer: (childJson: any, done: Function, context: any) => {
      object(modelClass).deserializer(
        childJson,
        (err: Error, result: any) => {
          const keepResult = allowNull && isNullOrUndefined(result);
          done(err, keepResult ? result : createImmutableMap(result));
        },
        context,
        undefined
      );
    }
  };
}

export function immutableWeakTypedMap(propSchema?: PropSchema): PropSchemaType {
  return {
    serializer: (mapObj: IImmutableWeakTypedMap<any>) => {
      if (mapObj === null || mapObj === undefined) {
        return mapObj;
      }
      // invariant(Map.isMap(mapObj), 'expected immutableMap')
      return (mapObj.map((obj: any) => object(propSchema as any).serializer(obj)) as any).toJSON();
    },
    deserializer: (jsonMap: Record<string, Object>, done: Function, context: any) => {
      if (jsonMap === null || jsonMap === undefined) {
        done(null, jsonMap);
        return;
      }
      const deserializeResult: Record<string, Object> = {};
      let lastError: any;
      Object.keys(jsonMap).forEach((key: string) => {
        if (!lastError) {
          if (propSchema) {
            propSchema.deserializer(
              jsonMap[key],
              (err: Error, result: any) => {
                if (err) {
                  lastError = err;
                }

                deserializeResult[key] = result;
              },
              context,
              undefined
            );
          } else {
            deserializeResult[key] = jsonMap[key];
          }
        }
      });

      if (lastError) {
        done(lastError, null);
      } else {
        done(null, createImmutableWeakTypedMap(deserializeResult));
      }
    }
  };
}

export function immutableDate(): PropSchemaType {
  return {
    serializer: (item: ImmutableDate) => {
      if (item === null || item === undefined) {
        return item;
      }
      return item.toISOString();
    },
    deserializer: (jsonValue: any, done: Function) => {
      if (jsonValue === null || jsonValue === undefined) {
        done(null, jsonValue);
        return;
      }
      done(null, createImmutableDate(jsonValue));
    }
  };
}

// Thoughts from here: https://github.com/mobxjs/serializr/issues/65

// Implementing custom serializr supporting DUs with 'type' field
export type UnionClazz = {
  type: string | number;
  new (...args: any[]): any;
};

export type GenericSchema<T> = Clazz<T> | UnionClazz;

export function discriminated<T extends UnionClazz>(...unionClazzes: UnionClazz[]): PropSchemaType {
  return {
    serializer: (sourcePropertyValue: T): any => serialize(sourcePropertyValue),
    deserializer: (jsonValue: any, callback: (err: any, result: {}) => void): void => {
      if (jsonValue === undefined || jsonValue === null) {
        return jsonValue;
      }

      // jsonValue is something that should be shaped like a T - but may not be
      // If it is a bad type, a runtime error will throw

      const discriminate = jsonValue.type;
      const clazz = unionClazzes.find((c: UnionClazz) => c.type === discriminate);

      if (!clazz) {
        callback(`Unknown type: ${discriminate}`, {});
      } else {
        if (Array.isArray(jsonValue)) {
          deserializeToImmutableList(clazz, jsonValue);
        } else {
          deserializeToImmutableMap(clazz, jsonValue, callback);
        }
      }
    }
  };
}
